import useOffer from 'hooks/Offers/useOffer'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { getItemUniqueKey } from 'checkout/lib/utils/accommodation/cart'
import { useMemo } from 'react'
import { isAccommodationCheckInDatePast } from 'lib/datetime/dateUtils'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'

export type UserMultiCartHotelItemData = {
  offer: App.HotelOffer
  loaded: true
  loading: false
  error: any
  package: App.HotelPackage
  itemTotals: App.Checkout.ItemViewTotals
  isPast: boolean
  isSoldOut: boolean
  isUnavailable: boolean
  isBookable: boolean
} | {
  offer: App.HotelOffer | undefined
  loaded: false
  loading: boolean
  error: any
  package: App.HotelPackage | undefined
  itemTotals: App.Checkout.ItemViewTotals | undefined
  isPast: boolean
  isSoldOut: boolean
  isUnavailable: boolean
  isBookable: boolean
}

function useUserMultiCartHotelItemData(item: App.Checkout.InstantBookingLEHotelItem): UserMultiCartHotelItemData {
  const [offer, offerLoading, offerError] = useOffer<App.HotelOffer>(item.offerId)

  const {
    calendarsByOccupancy,
    calendarsByOccupancyLoading,
    availableRatesByOccupancy,
  } = useAppSelector((state) => ({
    calendarsByOccupancy: state.calendar.calendarsByOccupancy,
    calendarsByOccupancyLoading: state.calendar.calendarsByOccupancyLoading,
    availableRatesByOccupancy: state.offer.offerAvailableRatesByOccupancy,
  }), shallowEqual)

  let pkg: App.HotelPackage | undefined
  if (offer) {
    const uniqueKey = getItemUniqueKey(item)
    pkg = offer.packages.find(pkg => pkg.uniqueKey === uniqueKey)
  }

  const propertyTimeZone = getAccommodationPropertyTimeZone(offer)
  const isOfferPending = !offer && !offerError
  const isPast = propertyTimeZone ? isAccommodationCheckInDatePast(
    item.checkIn,
    { timeZone: propertyTimeZone },
  ) : false

  const isUnavailable = useMemo(() => {
    const endDate = offer?.availabilitySchedule?.end
    if (!endDate) return false
    return new Date() > new Date(endDate)
  }, [offer?.availabilitySchedule?.end])

  const baseBookabilityCheck = !isPast && !offer?.isSoldOut && !isUnavailable && !offerError

  let itemTotals: App.Checkout.ItemViewTotals | undefined
  let calendarsLoading = false
  let availableRatesLoading = false

  if (offer && pkg) {
    if (isInstantBookingLEHotelItem(item)) {
      const occupancyKey = generateOccupancyStringByRoom(item.occupancy)
      calendarsLoading = calendarsByOccupancyLoading[occupancyKey]?.[item.offerId]

      if (offer.type === OFFER_TYPE_ALWAYS_ON && item.checkIn && item.checkOut) {
        const key = buildAvailableRateKey(item.checkIn, item.checkOut, [item.occupancy])
        availableRatesLoading = availableRatesByOccupancy[offer.id]?.[key]?.fetching
      }

      itemTotals = getLEPackageItemTotals(
        offer,
        pkg,
        item.checkIn,
        item.checkOut,
        item.occupancy,
        undefined,
        calendarsByOccupancy,
        availableRatesByOccupancy,
      )
    } else {
      itemTotals = {
        price: pkg.price,
        memberPrice: pkg.memberPrice,
        value: pkg.value,
        taxesAndFees: pkg.taxesAndFees,
        otherFees: {
          propertyFees: pkg.propertyFees,
        },
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }
  }

  const loading = offerLoading || calendarsLoading || availableRatesLoading || isOfferPending
  const isSoldOut = !!(offer?.isSoldOut || !itemTotals?.isAvailable)

  if (offer && pkg && itemTotals && !calendarsLoading) {
    return {
      offer,
      loaded: true,
      loading: false,
      error: offerError,
      package: pkg,
      itemTotals,
      isPast,
      isSoldOut,
      isUnavailable,
      isBookable: !!(baseBookabilityCheck && itemTotals.isAvailable),
    }
  }

  return {
    offer,
    loaded: false,
    loading,
    error: offerError,
    package: pkg,
    itemTotals,
    isPast,
    isSoldOut,
    isUnavailable,
    isBookable: baseBookabilityCheck,
  }
}

export default useUserMultiCartHotelItemData
